import type { CardCorners } from 'blocks/Cards'
import type { IPartnerBannerProps } from './PartnerBanner'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { FallbackImageSC } from 'atoms/FallbackImage'
import { IconSC, IconSizes } from 'atoms/Icon'
import { PosterSC } from 'atoms/ImagePoster'
import {
  PartnerProgramCoverDesktopSC,
  PartnerProgramCoverMobileSC,
} from 'atoms/PartnerProgramCover'
import { LiveEventDescriptionSC } from 'blocks/Description/Templates'
import {
  BANNER_ASPECT_RATIO,
  BANNER_BACK_BUTTON_INDENT,
  COLOR,
  FIXED_INDENTS,
  LAYOUT,
  MEDIA,
  NAVBAR_HEIGHT,
  PAGE_NOTIFICATION,
} from 'vars/index'
import {
  PartnerBannerAlignItems,
  PartnerBannerJustifyContent,
  PartnerBannerOverlay,
} from './PartnerBanner.h'

export const OverlayLayout: any = styled.div`
  display: ${({ overlayEnabled }) => (overlayEnabled ? 'block' : 'none')};
  width: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.POSTER ? 'initial' : '100%'};
  height: 100%;
  aspect-ratio: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.POSTER
      ? `var(${aspectRatio})`
      : 'initial'};
  position: absolute;
  top: 0;
  right: 0;
  background: ${({ overlay, overlayDirection }) =>
    overlay &&
    (overlayDirection === 'OVERLAY_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_VERTICAL_FULL')
      ? `var(${overlay})`
      : PartnerBannerOverlay.None};

  ${MEDIA.MAX_1239} {
    width: 100%;
    height: 100%;
    aspect-ratio: initial;
  }

  &:before {
    display: ${({ overlayDirection }) =>
      overlayDirection === 'OVERLAY_VERTICAL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL' ||
      overlayDirection === 'OVERLAY_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL'
        ? 'block'
        : 'none'};
    content: '';
    width: 100%;
    height: ${({ overlayHeight }) => `${overlayHeight || 0}%`};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${({ overlayVertical }) => `var(${overlayVertical})`};
  }

  &:after {
    display: ${({ overlayDirection }) =>
      overlayDirection === 'OVERLAY_HORIZONTAL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL'
        ? 'block'
        : 'none'};
    content: '';
    width: ${({ overlayWidth }) => `${overlayWidth || 0}%`};
    transform: translateX(-1px);
    height: 100%;
    position: absolute;
    background: ${({ overlayHorizontal, partialOverlay }) =>
      partialOverlay ? `var(${partialOverlay})` : `var(${overlayHorizontal})`};
  }
`

export const BannerContent = styled.div<{
  justifyContent?: IPartnerBannerProps['justifyContent']
  alignItems?: IPartnerBannerProps['alignItems']
  paddings?: IPartnerBannerProps['paddings']
  backButtonIndent?: IPartnerBannerProps['backButtonIndent']
}>`
  --bannerPaddingBottom: var(${FIXED_INDENTS.INDENT_90});

  ${MEDIA.MIN_1240_OR_LANDSCAPE} {
    --bannerPaddingBottom: var(${FIXED_INDENTS.INDENT_140});
  }

  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) =>
    alignItems || PartnerBannerAlignItems.Start};
  justify-content: ${({ justifyContent }) =>
    justifyContent || PartnerBannerJustifyContent.Flex_start};
  padding: ${({ paddings }) => (paddings?.length ? `var(${paddings}) ` : 0)};

  /* Back button styles */
  > ${ButtonSC} {
    position: absolute;
    top: ${({ backButtonIndent }) =>
      `var(${backButtonIndent ?? BANNER_BACK_BUTTON_INDENT.SIZE_20})`};
  }
`

export const Image = styled.div<{
  aspectRatio?: BANNER_ASPECT_RATIO | null
  noContentInside?: boolean
}>`
  position: relative;
  width: 100%;

  ${MEDIA.MIN_1240_OR_LANDSCAPE} {
    width: ${({ aspectRatio }) =>
      aspectRatio === BANNER_ASPECT_RATIO.POSTER ? 'initial' : '100%'};
    height: 100%;
    aspect-ratio: ${({ aspectRatio }) =>
      aspectRatio === BANNER_ASPECT_RATIO.POSTER
        ? `var(${aspectRatio})`
        : 'initial'};
  }

  top: ${({ noContentInside }) =>
    noContentInside ? `var(${NAVBAR_HEIGHT.DEFAULT})` : `0`};
  right: 0;

  ${PosterSC} {
    width: 100%;
    height: 100%;
    object-fit: contain;

    ${MEDIA.MIN_1240_OR_LANDSCAPE} {
      object-fit: cover;
    }
  }

  ${FallbackImageSC} {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(${COLOR.TEXT_ICON_CONSTANT_DARK});
    color: var(${COLOR.DISABLED});
  }

  ${PartnerProgramCoverDesktopSC} {
    display: none;

    ${MEDIA.MIN_1240_OR_LANDSCAPE} {
      display: block;
    }
  }

  ${PartnerProgramCoverMobileSC} {
    display: block;

    ${MEDIA.MIN_1240_OR_LANDSCAPE} {
      display: none;
    }
  }
`

export const FallbackCover = styled.div`
  width: 100%;
  height: 100%;
  animation: partnerBannerFallbackFadeIn 0.3s ease;

  @keyframes partnerBannerFallbackFadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`

export const BannerSC = styled.div<{
  width: IPartnerBannerProps['width']
  aspectRatio: IPartnerBannerProps['aspectRatio']
  cursor: IPartnerBannerProps['cursor']
  corners?: CardCorners
}>`
  --partnerBannerViewportUnit: 100vh;
  --partnerBannerViewportHeight: calc(
    var(--partnerBannerViewportUnit) - var(${NAVBAR_HEIGHT.DEFAULT}) - var(
        ${PAGE_NOTIFICATION.HEIGHT},
        0px
      )
  );

  @supports (height: 100dvh) {
    --partnerBannerViewportUnit: 100dvh;
  }

  padding-bottom: 0 !important;
  background-color: var(${COLOR.BACKGROUND_DARK});
  width: ${({ width }) => (width ? `var(${width})` : `0`)};
  max-width: 100%;
  position: relative;
  border-radius: ${({ corners }) => `var(${corners})`};

  /* Solution for browser versions, where the 'aspect-ratio' property is not supported */
  @supports not (
    aspect-ratio: ${({ aspectRatio }) =>
        aspectRatio ? `var(${aspectRatio})` : `0`}
  ) {
    &::before {
      float: left;
      padding-top: ${({ aspectRatio }) => `calc(100% / var(${aspectRatio}))`};
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }

  min-height: var(--partnerBannerViewportHeight);

  ${LiveEventDescriptionSC} {
    max-height: initial;
  }

  ${Image} {
    aspect-ratio: ${({ aspectRatio }) =>
      aspectRatio ? `var(${aspectRatio})` : `0`};
  }

  ${MEDIA.MIN_1240_OR_LANDSCAPE} {
    aspect-ratio: ${({ aspectRatio }) =>
      aspectRatio ? `var(${aspectRatio})` : `0`};
    min-height: auto;
    // styles for liveEvents details page
    height: var(--partnerBannerViewportHeight);

    ${Image} {
      aspect-ratio: initial;
    }
  }

  cursor: ${({ cursor }) => cursor || 'initial'};
  ${MEDIA.MAX_1239_LANDSCAPE} {
    ${BannerContent} {
      height: 100%;
      min-height: calc(
        100% - var(${LAYOUT.LoaderHeight})
      ); //navigation loader heigth
    }
  }

  ${OverlayLayout} {
    display: none;
  }

  ${BannerContent} {
    display: none;
  }

  ${Image} {
    ${OverlayLayout} {
      display: block;
    }

    ${BannerContent} {
      display: flex;
    }
  }

  ${MEDIA.MIN_1240_OR_LANDSCAPE} {
    ${OverlayLayout} {
      display: block;
    }

    ${BannerContent} {
      display: flex;
    }

    ${Image} {
      ${OverlayLayout} {
        display: none;
      }

      ${BannerContent} {
        display: none;
      }
    }
  }
`

export const LockedImage = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  ${IconSC} {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 15.5%;

    ${MEDIA.MAX_1439} {
      font-size: ${() => IconSizes.xBigger};
      right: 11.4%;
    }

    ${MEDIA.MAX_767} {
      right: 5.1%;
    }

    ${MEDIA.MAX_599} {
      top: var(${FIXED_INDENTS.INDENT_200});
      right: 50%;
      transform: translateY(0) translateX(50%);
    }
  }
`
